import type {
  MfaSetup,
  MfaStatus,
  SecondFactorRequired,
  Session,
  User,
} from '../../shared/api'
import { api } from '../../shared/api'

import { useAuthStore } from './hooks'

/** Signs in, or returns the challenge when the account asks for a code (ADR-073). */
export async function login(email: string, password: string) {
  const result = await api<Session | SecondFactorRequired>('/auth/login', {
    method: 'POST',
    body: { email, password },
  })

  if ('mfaRequired' in result) return result
  useAuthStore.getState().setSession(result)

  return result
}

export async function verifySecondFactor(mfaToken: string, code: string) {
  const session = await api<Session>('/auth/2fa/verify', {
    method: 'POST',
    body: { mfaToken, code },
  })

  useAuthStore.getState().setSession(session)

  return session
}

export const mfaApi = {
  status: () => api<MfaStatus>('/auth/me/2fa'),
  setup: () => api<MfaSetup>('/auth/me/2fa/setup', { method: 'POST' }),
  enable: (code: string) =>
    api<{ recoveryCodes: string[] }>('/auth/me/2fa/enable', {
      method: 'POST',
      body: { code },
    }),
  disable: (code: string) =>
    api<void>('/auth/me/2fa/disable', { method: 'POST', body: { code } }),
  recoveryCodes: (code: string) =>
    api<{ recoveryCodes: string[] }>('/auth/me/2fa/recovery-codes', {
      method: 'POST',
      body: { code },
    }),
}

export async function logout() {
  const csrf = document.cookie
    .split('; ')
    .find((c) => c.startsWith('cms_csrf='))
    ?.slice(9)

  await fetch('/api/v1/auth/logout', {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrf ? { 'x-csrf-token': decodeURIComponent(csrf) } : {},
  }).catch(() => undefined)
  useAuthStore.getState().clear()
}

export const updateProfile = (displayName: string) =>
  api<User>('/auth/me', { method: 'PATCH', body: { displayName } })
export const changePassword = (currentPassword: string, newPassword: string) =>
  api<void>('/auth/change-password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  })
